import { Rule } from "@dashboard/discounts/models";
import {
  PromotionTypeEnum,
  RewardTypeEnum,
  RewardValueTypeEnum,
} from "@dashboard/graphql";
import { defineMessages, IntlShape } from "react-intl";
import * as z from "zod";

const validationMessages = defineMessages({
  nameRequired: {
    id: "7Hdiw2",
    defaultMessage: "Rule name is required",
  },
  channelRequired: {
    id: "y08GTW",
    defaultMessage: "Channel is required",
  },
  rewardValueRequired: {
    id: "uf1ttM",
    defaultMessage: "Rule reward value is required",
  },
  rewardValueMustBeLessThan100: {
    id: "DDYrvn",
    defaultMessage: "Rule reward value must be less than 100",
  },
  rewardGiftRequired: {
    id: "GxsQuO",
    defaultMessage: "You must select at least one gift",
  },
  conditionValueRequired: {
    id: "Tb17T6",
    defaultMessage: "Condition value is required",
  },
  orderConditionRequired: {
    id: "rWTZn6",
    defaultMessage: "Order rule must have at least one condition",
  },
});

const option = z.object({ label: z.string(), value: z.string() });
const rewardValueRequired = (intl: IntlShape) =>
  z
    .number({
      required_error: intl.formatMessage(
        validationMessages.rewardValueRequired,
      ),
      invalid_type_error: intl.formatMessage(
        validationMessages.rewardValueRequired,
      ),
    })
    .min(1, intl.formatMessage(validationMessages.rewardValueRequired));

const getDefaultSchema = (intl: IntlShape) =>
  z.object({
    id: z.string().optional(),
    description: z.string().nullable(),
    hasPredicateNestedConditions: z.boolean().optional(),
    name: z
      .string()
      .min(1, intl.formatMessage(validationMessages.nameRequired)),
    channel: z.object(
      { label: z.string(), value: z.string() },
      {
        required_error: intl.formatMessage(validationMessages.channelRequired),
        invalid_type_error: intl.formatMessage(
          validationMessages.channelRequired,
        ),
      },
    ),
    conditions: z.array(
      z.object({
        id: z.string().nullable(),
        type: z.string(),
        value: z
          .array(option)
          .or(z.string())
          .or(z.tuple([z.string(), z.string()]))
          .or(z.null()),
      }),
    ),
  });

const getCatalogRewardValidation = (intl: IntlShape) =>
  z.object({
    rewardValue: rewardValueRequired(intl),
    rewardValueType: z.string(),
    rewardType: z.literal(null),
    rewardGifts: z.array(option).optional(),
  });

const getOrderRewardSubtotalValidation = (intl: IntlShape) => {
  return z.object({
    rewardValue: rewardValueRequired(intl),
    rewardValueType: z.string(),
    rewardType: z.literal(RewardTypeEnum.SUBTOTAL_DISCOUNT),
    rewardGifts: z.array(option).optional(),
  });
};

const getOrderRewardGiftsValidation = (intl: IntlShape) =>
  z.object({
    rewardValue: z.null(),
    rewardValueType: z.string(),
    rewardType: z.literal(RewardTypeEnum.GIFT),
    rewardGifts: z.array(option).nonempty({
      message: intl.formatMessage(validationMessages.rewardGiftRequired),
    }),
  });

// Values loaded from the API can be numbers, so don't assume strings
export const isConditionValueEmpty = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    // Empty multiselect, or a range with a missing bound
    return value.length === 0 || value.some(isConditionValueEmpty);
  }

  if (value && typeof value === "object") {
    // A selected option
    return false;
  }

  return value === null || value === undefined || String(value).trim() === "";
};

// Order rules: preparePredicate drops conditions without a value, and an
// empty discountedObjectPredicate makes every order qualify. Rules with
// nested conditions keep their API predicate, so they are not checked.
const validateOrderConditions = (
  intl: IntlShape,
  { conditions, hasPredicateNestedConditions }: Rule,
  ctx: z.RefinementCtx,
) => {
  if (hasPredicateNestedConditions) {
    return;
  }

  const namedConditions = conditions.filter(condition => !!condition.id);

  if (namedConditions.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: intl.formatMessage(validationMessages.orderConditionRequired),
      path: ["conditions"],
    });
  }

  conditions.forEach((condition, index) => {
    if (condition.id && isConditionValueEmpty(condition.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: intl.formatMessage(validationMessages.conditionValueRequired),
        path: ["conditions", index, "value"],
      });
    }
  });
};

export const getValidationSchema = (
  intl: IntlShape,
  discountType?: PromotionTypeEnum,
) => {
  const schemaCond = z.discriminatedUnion("rewardType", [
    getCatalogRewardValidation(intl),
    getOrderRewardSubtotalValidation(intl),
    getOrderRewardGiftsValidation(intl),
  ]);

  return z
    .intersection(schemaCond, getDefaultSchema(intl))
    .refine(
      ({ rewardValue, rewardValueType, channel }) => {
        if (
          channel &&
          rewardValueType === RewardValueTypeEnum.PERCENTAGE &&
          Number(rewardValue) > 100
        ) {
          return false;
        }

        return true;
      },
      {
        message: intl.formatMessage(
          validationMessages.rewardValueMustBeLessThan100,
        ),
        path: ["rewardValue"],
      },
    )
    .superRefine((rule, ctx) => {
      if (discountType === PromotionTypeEnum.ORDER) {
        validateOrderConditions(intl, rule as Rule, ctx);
      }
    });
};
